import React from 'react';
import { FlexibleCRUDTable } from '@/components/common/FlexibleCRUDTable';
import { DynamicForm } from '@/components/common/DynamicForm';
import { ViewModal } from '@/components/common/ViewModal';
import { abacCustomAttributeEntity } from '@/config/entities/abacCustomAttributes';
import { abacCustomAttributeGenericService } from '@/services/abac';
import { toast } from 'react-hot-toast';
import { useLanguage } from '@/state/language/LanguageProvider';
import { ArrowPathIcon } from '@heroicons/react/24/outline';

const CustomAttributesPage: React.FC = () => {
  const { t } = useLanguage();
  const [showForm, setShowForm] = React.useState(false);
  const [editingItem, setEditingItem] = React.useState<any>(null);
  const [viewingItem, setViewingItem] = React.useState<any>(null);
  const [showViewModal, setShowViewModal] = React.useState(false);

  const handleCreate = () => {
    setEditingItem(null);
    setShowForm(true);
  };

  const handleEdit = (item: any) => {
    setEditingItem(item);
    setShowForm(true);
  };

  const handleView = (item: any) => {
    setViewingItem(item);
    setShowViewModal(true);
  };

  const handleToggleStatus = async (id: string, isActive: boolean) => {
    try {
      await abacCustomAttributeGenericService.toggleStatus(id, isActive);
      toast.success('Custom attribute status updated successfully');
    } catch (error) {
      toast.error('Failed to update custom attribute status');
    }
  };

  const handleFormSubmit = async (data: any) => {
    try {
      if (editingItem) {
        await abacCustomAttributeGenericService.update(editingItem.id, data);
        toast.success('Custom attribute updated successfully');
      } else {
        await abacCustomAttributeGenericService.create(data);
        toast.success('Custom attribute created successfully');
      }
      setShowForm(false);
      setEditingItem(null);
    } catch (error) {
      toast.error('Failed to save custom attribute');
    }
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingItem(null);
  };

  const handleViewModalClose = () => {
    setShowViewModal(false);
    setViewingItem(null);
  };

  return (
    <div className="container mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">{t('abac.customAttributesManagement')}</h1>
        <p className="text-gray-600 mt-2">
          {t('abac.customAttributesDescription')}
        </p>
      </div>

      {showForm ? (
        <DynamicForm
          entity={abacCustomAttributeEntity}
          initialData={editingItem}
          onSubmit={handleFormSubmit}
          onCancel={handleFormCancel}
        />
      ) : (
        <FlexibleCRUDTable
          entity={abacCustomAttributeEntity}
          service={abacCustomAttributeGenericService}
          showSearch={true}
          onView={handleView}
          onEdit={handleEdit}
          onDelete={async (item: any) => {
            if (window.confirm(`Are you sure you want to delete "${item.attribute_name}"?`)) {
              try {
                await abacCustomAttributeGenericService.delete(item.id);
                toast.success('Custom attribute deleted successfully');
              } catch (error) {
                toast.error('Failed to delete custom attribute');
              }
            }
          }}
          onCreate={handleCreate}
          showPagination={true}
          searchPlaceholder="Search custom attributes..."
          customActions={[
            {
              label: 'Toggle Status',
              icon: ArrowPathIcon,
              onClick: async (item: any) => {
                try {
                  await handleToggleStatus(item.id, item.is_active !== true);
                } catch (error) {
                  console.error('Toggle status error:', error);
                }
              }
            }
          ]}
          onOperationComplete={() => {
            // This will be called after successful operations (create, update, delete)
            // The table will automatically refresh via loadData() in the component
          }}
        />
      )}

      {/* View Modal */}
      <ViewModal
        isOpen={showViewModal}
        onClose={handleViewModalClose}
        title={`View Custom Attribute: ${viewingItem?.attribute_name || ''}`}
        data={viewingItem || {}}
        fields={[
          { key: 'attribute_name', label: 'Attribute Name', type: 'string' },
          { key: 'attribute_type', label: 'Attribute Type', type: 'string' },
          { key: 'data_type', label: 'Data Type', type: 'string' },
          { key: 'description', label: 'Description', type: 'string' },
          { key: 'is_required', label: 'Required', type: 'boolean' },
          { key: 'is_active', label: 'Active', type: 'boolean' },
          { key: 'computation_type', label: 'Computation Type', type: 'string' },
          { key: 'default_value', label: 'Default Value', type: 'string' },
          { key: 'created_at', label: 'Created At', type: 'datetime' },
          { key: 'updated_at', label: 'Updated At', type: 'datetime' }
        ]}
      />
    </div>
  );
};

export default CustomAttributesPage;
